import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Pagination,
  Grid,
  Card,
  CardContent,
  Link,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { generateSignedUrlForFilename } from '../../../utils/s3Utils';

const GSTExemption = () => {
  const [filters, setFilters] = useState({
    labType: '',
    userName: '',
    transactionStatus: '',
    dateFrom: null,
    dateTo: null
  });

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: '', // 'accept', 'reject', 'rollback'
    appId: null,
    remarks: ''
  });

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const labTypes = [
    { value: '0', label: '--Select--' },
    { value: '1', label: 'Testing' },
    { value: '2', label: 'Calibration' },
    { value: '3', label: 'Medical' },
    { value: '4', label: 'PTP' },
    { value: '5', label: 'RMP' }
  ];

  const transactionStatuses = [
    { value: '1', label: 'Accepted' },
    { value: '2', label: 'Rejected' },
    { value: '0', label: 'Pending' }
  ];

  useEffect(() => {
    fetchApplications();
  }, [page, filters]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page,
        pageSize: pageSize
      });

      if (filters.labType && filters.labType !== '0') {
        params.append('labType', filters.labType);
      }

      if (filters.userName) {
        params.append('userName', filters.userName);
      }

      if (filters.transactionStatus) {
        params.append('transactionStatus', filters.transactionStatus);
      }

      if (filters.dateFrom && filters.dateTo) {
        params.append('dateFrom', filters.dateFrom.toISOString());
        params.append('dateTo', filters.dateTo.toISOString());
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/gst-exemption?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Debug logging to see the structure of the data
        console.log('GST Exemption data received:', data);
        console.log('Sample application:', data.data?.[0]);
        
        setApplications(data.data || []);
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        throw new Error(data.message || 'Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      showAlert('Error fetching GST exemption applications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filter changes
  };

  const handleResetFilters = () => {
    setFilters({
      labType: '',
      userName: '',
      transactionStatus: '',
      dateFrom: null,
      dateTo: null
    });
    setPage(1);
  };

  const handleDateSearch = () => {
    if (!filters.dateFrom || !filters.dateTo) {
      showAlert('Please select both from and to dates', 'warning');
      return;
    }
    fetchApplications();
  };

  const handleAccept = async () => {
    const appId = confirmDialog.appId;
    const orgId = localStorage.getItem('orgId');
    
    // Debug logging
    console.log('Accept GST Exemption Debug:', {
      appId,
      orgId,
      appIdType: typeof appId,
      orgIdType: typeof orgId
    });

    // Validate required fields before sending
    if (!appId) {
      showAlert('Application ID is missing', 'error');
      return;
    }

    if (!orgId) {
      showAlert('Organization ID is missing. Please login again.', 'error');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/gst-exemption/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          appId: appId.toString(), // Ensure it's a string
          orgId: orgId.toString()  // Ensure it's a string
        })
      });

      const data = await response.json();
      
      if (data.success) {
        showAlert('Accepted successfully', 'success');
        setConfirmDialog({ open: false, type: '', appId: null, remarks: '' });
        fetchApplications();
      } else {
        throw new Error(data.message || 'Failed to accept application');
      }
    } catch (error) {
      console.error('Error accepting application:', error);
      showAlert('Error accepting application: ' + error.message, 'error');
    }
  };

  const handleReject = async () => {
    const appId = confirmDialog.appId;
    const orgId = localStorage.getItem('orgId');
    
    // Debug logging
    console.log('Reject GST Exemption Debug:', {
      appId,
      orgId,
      remarks: confirmDialog.remarks,
      appIdType: typeof appId,
      orgIdType: typeof orgId
    });

    // Validate required fields before sending
    if (!appId) {
      showAlert('Application ID is missing', 'error');
      return;
    }

    if (!orgId) {
      showAlert('Organization ID is missing. Please login again.', 'error');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/gst-exemption/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          appId: appId.toString(), // Ensure it's a string
          orgId: orgId.toString(),  // Ensure it's a string
          remarks: confirmDialog.remarks || ''
        })
      });

      const data = await response.json();
      
      if (data.success) {
        showAlert('Rejected successfully', 'success');
        setConfirmDialog({ open: false, type: '', appId: null, remarks: '' });
        fetchApplications();
      } else {
        throw new Error(data.message || 'Failed to reject application');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      showAlert('Error rejecting application: ' + error.message, 'error');
    }
  };

  const handleRollback = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/gst-exemption/rollback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          appId: confirmDialog.appId,
          orgId: localStorage.getItem('orgId')
        })
      });

      const data = await response.json();
      
      if (data.success) {
        showAlert('Rollback completed successfully', 'success');
        setConfirmDialog({ open: false, type: '', appId: null, remarks: '' });
        fetchApplications();
      } else {
        throw new Error(data.message || 'Failed to rollback application');
      }
    } catch (error) {
      console.error('Error rolling back application:', error);
      showAlert('Error rolling back application: ' + error.message, 'error');
    }
  };

  const handleDownloadExcel = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (filters.labType && filters.labType !== '0') {
        params.append('labType', filters.labType);
      }

      if (filters.userName) {
        params.append('userName', filters.userName);
      }

      if (filters.transactionStatus) {
        params.append('transactionStatus', filters.transactionStatus);
      }

      if (filters.dateFrom && filters.dateTo) {
        params.append('dateFrom', filters.dateFrom.toISOString());
        params.append('dateTo', filters.dateTo.toISOString());
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/gst-exemption/export?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'GST_Exemption_Report.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showAlert('Excel file downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      showAlert('Error downloading Excel file', 'error');
    }
  };

  const showAlert = (message, severity = 'info') => {
    setAlert({ open: true, message, severity });
  };

  const handleViewFile = async (fileName) => {
    if (!fileName) {
      showAlert('No file available to view', 'warning');
      return;
    }

    try {
      setLoading(true);
      // Generate signed URL for the file
      const signedUrl = await generateSignedUrlForFilename(fileName);
      
      if (signedUrl) {
        // Open the signed URL in a new tab
        window.open(signedUrl, '_blank');
      } else {
        showAlert('Unable to generate file access URL', 'error');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      showAlert('Error accessing file: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      0: { color: 'warning', label: 'Pending' },
      1: { color: 'success', label: 'Accepted' },
      2: { color: 'error', label: 'Rejected' }
    };
    
    const statusInfo = statusMap[status] || { color: 'default', label: status };
    
    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const renderActionButtons = (application) => {
    const status = application.approval || 0;

    // Debug logging for application structure
    console.log('Application data for action buttons:', {
      AppId: application.AppId,
      appid: application.appid,
      id: application.id,
      ID: application.ID,
      fullObject: application
    });

    if (status === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CheckIcon />}
            onClick={() => {
              console.log('Accept button clicked for AppId:', application.AppId || application.appid || application.ID);
              setConfirmDialog({ 
                open: true, 
                type: 'accept',
                appId: application.AppId || application.appid || application.ID, 
                remarks: '' 
              });
            }}
          >
            Accept
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<CloseIcon />}
            onClick={() => {
              console.log('Reject button clicked for AppId:', application.AppId || application.appid || application.ID);
              setConfirmDialog({ 
                open: true, 
                type: 'reject',
                appId: application.AppId || application.appid || application.ID, 
                remarks: '' 
              });
            }}
          >
            Reject
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width:250 , gap: 1 }}>
        <Typography variant='body2' color='primary' fontSize='small' fontWeight='600' >
          Submitted Date: {application.submitdate ? new Date(application.submitdate).toLocaleString('en-GB') : ''}
         </Typography>
        <Typography variant='body2' color='primary' fontSize='small' fontWeight='600' >
         Action Date: {application.date ? new Date(application.date).toLocaleString('en-GB') : ''}
         </Typography>
        {getStatusChip(status)}
        {application.approval === 2 && (
         <Typography variant='caption' color='error' fontSize='small' >
           {application.remarks}
         </Typography>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          GST Exemption Management
        </Typography>

        {/* Filters Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FilterIcon />
            <Typography variant="h6">Filters</Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExcel}
            >
              Download Excel
            </Button> */}
          </Box>

          <Grid container spacing={2}>
            {/* Transaction Status Radio Buttons */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                  Transaction Status:
                </Typography>
                <RadioGroup
                  row
                  value={filters.transactionStatus}
                  onChange={(e) => handleFilterChange('transactionStatus', e.target.value)}
                >
                  {transactionStatuses.map((status) => (
                    <FormControlLabel
                      key={status.value}
                      value={status.value}
                      control={<Radio />}
                      label={status.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Lab Type */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Lab Type</InputLabel>
                <Select
                  value={filters.labType}
                  label="Lab Type"
                  onChange={(e) => handleFilterChange('labType', e.target.value)}
                >
                  {labTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* User Name */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="User Name"
                value={filters.userName}
                onChange={(e) => handleFilterChange('userName', e.target.value)}
                placeholder="Enter Username"
              />
            </Grid>

            {/* Date Range */}
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #0094ff', p: 2, borderRadius: 1, display: 'none' }}>
                <Grid container spacing={2} alignItems="end">
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Date From"
                      value={filters.dateFrom}
                      onChange={(date) => handleFilterChange('dateFrom', date)}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Date To"
                      value={filters.dateTo}
                      onChange={(date) => handleFilterChange('dateTo', date)}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      onClick={handleDateSearch}
                      fullWidth
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>

        {/* Applications Table */}
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>S. No</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Lab ID</TableCell>
                <TableCell>Laboratory Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Lab Type</TableCell>
                <TableCell>GST Exemption File</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>State</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Pincode</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="h6">No Data Found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((application, index) => (
                  <TableRow key={application.AppId || index}>
                    <TableCell>{(page - 1) * pageSize + index + 1}</TableCell>
                    <TableCell>{renderActionButtons(application)}</TableCell>
                    <TableCell>{application.ApplicationNo}</TableCell>
                    <TableCell>{application.LaboratoryName}</TableCell>
                    <TableCell>{application.loginname}</TableCell>
                    <TableCell>{application.LabType}</TableCell>
                    <TableCell>
                      {application.GSTExemptionFile && (
                        <Tooltip title="View/Download GST File">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewFile(application.GSTExemptionFile)}
                            disabled={loading}
                          >
                            <GetAppIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{application.ContactEmail}</TableCell>
                    <TableCell>{application.ContactMobile}</TableCell>
                    <TableCell>{application.Address}</TableCell>
                    <TableCell>{application.State}</TableCell>
                    <TableCell>{application.City}</TableCell>
                    <TableCell>{application.Pin}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Confirmation Dialog */}
        <Dialog 
          open={confirmDialog.open} 
          onClose={() => setConfirmDialog({ open: false, type: '', appId: null, remarks: '' })}
        >
          <DialogTitle>
            {confirmDialog.type === 'accept' && 'Confirm Accept GST Exemption'}
            {confirmDialog.type === 'reject' && 'Confirm Reject GST Exemption'}
            {confirmDialog.type === 'rollback' && 'Confirm Rollback GST Exemption'}
          </DialogTitle>
          <DialogContent>
            {confirmDialog.type === 'accept' && (
              <Typography>
                Are you sure you want to accept this GST exemption application?
              </Typography>
            )}
            
            {confirmDialog.type === 'reject' && (
              <>
                <Typography gutterBottom>
                  Are you sure you want to reject this GST exemption application?
                </Typography>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Remarks (Optional)"
                  multiline
                  rows={3}
                  fullWidth
                  value={confirmDialog.remarks}
                  onChange={(e) => setConfirmDialog(prev => ({ ...prev, remarks: e.target.value }))}
                />
              </>
            )}
            
            {confirmDialog.type === 'rollback' && (
              <Typography>
                Are you sure you want to rollback this GST exemption application?
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, type: '', appId: null, remarks: '' })}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (confirmDialog.type === 'accept') {
                  handleAccept();
                } else if (confirmDialog.type === 'reject') {
                  handleReject();
                } else if (confirmDialog.type === 'rollback') {
                  handleRollback();
                }
              }}
              variant="contained"
              color={
                confirmDialog.type === 'accept' ? 'primary' : 
                confirmDialog.type === 'reject' ? 'error' : 'info'
              }
            >
              {confirmDialog.type === 'accept' && 'Accept'}
              {confirmDialog.type === 'reject' && 'Reject'}
              {confirmDialog.type === 'rollback' && 'Rollback'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alert Snackbar */}
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={() => setAlert(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setAlert(prev => ({ ...prev, open: false }))}
            severity={alert.severity}
            sx={{ width: '100%' }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default GSTExemption;